import React, { useState, useEffect, useRef } from 'react';
import { 
  XMarkIcon,
  DocumentTextIcon, 
  CloudArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  TrashIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

interface Lead {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  leadUid?: string;
}

interface DocumentSubmission {
  id: number;
  fileName: string;
  fileSize: number;
  fileType: string;
}

interface DocumentRequestItem {
  id: number;
  requestId: number;
  documentTypeId: number;
  status: 'pending' | 'submitted' | 'accepted' | 'rejected';
  rejectionReason?: string;
  documentType: {
    name: string;
    description?: string;
    isRequired: boolean;
    maxFileSizeMb: number;
    allowedFileTypes: string[];
  };
  submissions?: DocumentSubmission[];
  notes?: Array<{ note: string; createdAt: string }>;
}

interface DocumentRequest {
  id: number;
  organizationId: number;
  leadId: number;
  requestToken: string;
  status: 'requested' | 'submitted' | 'reviewed' | 'rejected';
  requestedAt: string;
  lead: Lead;
  items: DocumentRequestItem[];
}

interface BulkManualDocumentUploadModalProps {
  onClose: () => void;
  onUploadSuccess: () => void;
}

interface FileUpload {
  file: File;
  preview: string;
  error?: string;
}

interface UploadResult {
  requestId: number;
  leadName: string;
  status: 'success' | 'failed' | 'partial';
  uploadedFiles: number;
  failedFiles: number;
  totalSize: number;
  error?: string;
  itemErrors?: Array<{
    documentType: string;
    fileName: string;
    error: string;
  }>;
}

const BulkManualDocumentUploadModal: React.FC<BulkManualDocumentUploadModalProps> = ({ 
  onClose, 
  onUploadSuccess 
}) => {
  const [requests, setRequests] = useState<DocumentRequest[]>([]);
  const [selectedRequests, setSelectedRequests] = useState<Set<number>>(new Set());
  const [uploads, setUploads] = useState<{ [requestId: number]: { [itemId: number]: FileUpload[] } }>({});
  const [notes, setNotes] = useState<{ [requestId: number]: { [itemId: number]: string } }>({});
  const [step, setStep] = useState<'selection' | 'upload' | 'progress' | 'results'>('selection');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('requested');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRequests, setExpandedRequests] = useState<Set<number>>(new Set());
  
  // Progress tracking
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    currentRequest: '',
    uploadedBytes: 0,
    totalBytes: 0
  });
  
  // Results
  const [results, setResults] = useState<UploadResult[]>([]);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        status: statusFilter,
        limit: '100'
      });

      const response = await api.get(`/documents/requests?${params}`);
      
      // Only show requests that have pending or rejected items
      const eligibleRequests = (response.data.data || []).filter((req: DocumentRequest) =>
        req.items.some(item => item.status === 'pending' || item.status === 'rejected')
      );
      
      setRequests(eligibleRequests);
    } catch (err: any) {
      console.error('Error fetching document requests:', err);
      setError('Failed to fetch document requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestToggle = (requestId: number) => {
    setSelectedRequests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
        // Clean up uploads and notes for this request
        setUploads(prevUploads => {
          const newUploads = { ...prevUploads };
          delete newUploads[requestId];
          return newUploads;
        });
        setNotes(prevNotes => {
          const newNotes = { ...prevNotes };
          delete newNotes[requestId];
          return newNotes;
        });
      } else {
        // Check if limit is reached
        if (newSet.size >= 20) {
          setError('Maximum 20 requests can be selected at once');
          return prev; // Don't modify the set
        }
        newSet.add(requestId);
        setError(null); // Clear error when successfully adding
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedRequests.size === filteredRequests.length && filteredRequests.length > 0) {
      setSelectedRequests(new Set());
      setError(null);
    } else {
      // Limit to 20 requests
      const requestsToSelect = filteredRequests.slice(0, 20).map(req => req.id);
      setSelectedRequests(new Set(requestsToSelect));
      
      if (filteredRequests.length > 20) {
        setError(`Only the first 20 requests have been selected (${filteredRequests.length} available). Maximum 20 requests allowed.`);
      } else {
        setError(null);
      }
    }
  };

  const toggleRequestExpanded = (requestId: number) => {
    setExpandedRequests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const handleFileSelect = (requestId: number, itemId: number, files: FileList | null) => {
    if (!files) return;

    const request = requests.find(r => r.id === requestId);
    const item = request?.items.find(i => i.id === itemId);
    if (!item) return;

    const newUploads: FileUpload[] = [];

    Array.from(files).forEach(file => {
      // Validate file size
      if (file.size > item.documentType.maxFileSizeMb * 1024 * 1024) {
        newUploads.push({
          file,
          preview: URL.createObjectURL(file),
          error: `File size exceeds ${item.documentType.maxFileSizeMb}MB limit`
        });
        return;
      }

      // Validate file type
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !item.documentType.allowedFileTypes.includes(fileExtension)) {
        newUploads.push({
          file,
          preview: URL.createObjectURL(file),
          error: `File type not allowed. Allowed: ${item.documentType.allowedFileTypes.join(', ')}`
        });
        return;
      }

      newUploads.push({
        file,
        preview: URL.createObjectURL(file)
      });
    });

    setUploads(prev => ({
      ...prev,
      [requestId]: {
        ...(prev[requestId] || {}),
        [itemId]: [...((prev[requestId] || {})[itemId] || []), ...newUploads]
      }
    }));
  };

  const removeFile = (requestId: number, itemId: number, index: number) => {
    setUploads(prev => {
      const newUploads = { ...prev };
      if (newUploads[requestId] && newUploads[requestId][itemId]) {
        const fileToRemove = newUploads[requestId][itemId][index];
        URL.revokeObjectURL(fileToRemove.preview);
        newUploads[requestId][itemId] = newUploads[requestId][itemId].filter((_, i) => i !== index);
        
        if (newUploads[requestId][itemId].length === 0) {
          delete newUploads[requestId][itemId];
        }
      }
      return newUploads;
    });
  };

  const handleNext = () => {
    if (selectedRequests.size === 0) {
      setError('Please select at least one document request');
      return;
    }
    
    setError(null);
    setStep('upload');
    
    // Auto-expand all selected requests
    setExpandedRequests(new Set(selectedRequests));
  };

  const handleBack = () => {
    setStep('selection');
    setError(null);
  };

  const handleSubmit = async () => {
    // Validation
    const selectedRequestsArray = Array.from(selectedRequests);
    
    // Check if at least one file is uploaded
    let hasFiles = false;
    for (const requestId of selectedRequestsArray) {
      const requestUploads = uploads[requestId];
      if (requestUploads) {
        for (const itemUploads of Object.values(requestUploads)) {
          if (itemUploads.some(u => !u.error)) {
            hasFiles = true;
            break;
          }
        }
      }
      if (hasFiles) break;
    }

    if (!hasFiles) {
      setError('Please upload at least one file');
      return;
    }

    // Check for validation errors
    const hasValidationErrors = selectedRequestsArray.some(requestId => {
      const requestUploads = uploads[requestId];
      if (!requestUploads) return false;
      
      return Object.values(requestUploads).some(itemUploads =>
        itemUploads.some(u => u.error)
      );
    });

    if (hasValidationErrors) {
      setError('Please fix all file validation errors before uploading');
      return;
    }

    // Calculate totals for confirmation
    let totalFiles = 0;
    let totalSize = 0;
    selectedRequestsArray.forEach(requestId => {
      const requestUploads = uploads[requestId];
      if (requestUploads) {
        Object.values(requestUploads).forEach(itemUploads => {
          itemUploads.forEach(upload => {
            if (!upload.error) {
              totalFiles++;
              totalSize += upload.file.size;
            }
          });
        });
      }
    });

    const confirmed = window.confirm(
      `You are about to upload ${totalFiles} file(s) (${formatFileSize(totalSize)}) for ${selectedRequestsArray.length} request(s). Continue?`
    );

    if (!confirmed) return;

    // Start upload process
    setStep('progress');
    setSubmitting(true);
    setError(null);
    
    const uploadResults: UploadResult[] = [];
    let completed = 0;

    setProgress({
      current: 0,
      total: selectedRequestsArray.length,
      currentRequest: '',
      uploadedBytes: 0,
      totalBytes: totalSize
    });

    // Upload sequentially for better tracking
    for (const requestId of selectedRequestsArray) {
      const request = requests.find(r => r.id === requestId);
      if (!request) continue;

      try {
        setProgress(prev => ({
          ...prev,
          current: completed,
          currentRequest: `${request.lead.firstName} ${request.lead.lastName}`
        }));

        const requestUploads = uploads[requestId];
        if (!requestUploads) {
          throw new Error('No uploads found for this request');
        }

        // Prepare FormData
        const formData = new FormData();
        
        const items = Object.entries(requestUploads)
          .map(([itemIdStr, fileUploads]) => {
            const itemId = parseInt(itemIdStr);
            const validFiles = fileUploads.filter(u => !u.error);
            
            if (validFiles.length === 0) return null;
            
            return {
              requestItemId: itemId,
              note: notes[requestId]?.[itemId] || '',
              fileCount: validFiles.length
            };
          })
          .filter(Boolean);

        if (items.length === 0) {
          throw new Error('No valid files to upload');
        }

        formData.append('items', JSON.stringify(items));

        // Add files in order
        Object.entries(requestUploads).forEach(([_, fileUploads]) => {
          fileUploads.forEach(upload => {
            if (!upload.error) {
              formData.append('files', upload.file);
            }
          });
        });

        // Upload
        const response = await api.post(
          `/documents/requests/${requestId}/manual-submit`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );

        if (response.data.success) {
          const uploadedFiles = items.reduce((sum, item) => sum + (item?.fileCount || 0), 0);
          const uploadedSize = Object.values(requestUploads)
            .flat()
            .reduce((sum, upload) => !upload.error ? sum + upload.file.size : sum, 0);

          uploadResults.push({
            requestId,
            leadName: `${request.lead.firstName} ${request.lead.lastName}`,
            status: 'success',
            uploadedFiles,
            failedFiles: 0,
            totalSize: uploadedSize
          });
        } else {
          throw new Error(response.data.message || 'Upload failed');
        }
      } catch (err: any) {
        console.error(`Error uploading for request ${requestId}:`, err);
        uploadResults.push({
          requestId,
          leadName: `${request.lead.firstName} ${request.lead.lastName}`,
          status: 'failed',
          uploadedFiles: 0,
          failedFiles: Object.values(uploads[requestId] || {}).flat().length,
          totalSize: 0,
          error: err.response?.data?.message || err.message || 'Upload failed'
        });
      }

      completed++;
      setProgress(prev => ({
        ...prev,
        current: completed
      }));
    }

    setResults(uploadResults);
    setSubmitting(false);
    setStep('results');
  };

  const handleRetryFailed = () => {
    // Filter to only keep failed requests
    const failedRequestIds = results
      .filter(r => r.status === 'failed')
      .map(r => r.requestId);
    
    setSelectedRequests(new Set(failedRequestIds));
    setStep('upload');
    setResults([]);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredRequests = requests.filter(req => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const leadName = `${req.lead.firstName} ${req.lead.lastName}`.toLowerCase();
    const email = req.lead.email.toLowerCase();
    const leadUid = req.lead.leadUid?.toLowerCase() || '';
    
    return leadName.includes(searchLower) || 
           email.includes(searchLower) || 
           leadUid.includes(searchLower);
  });

  const selectedRequestsData = requests.filter(req => selectedRequests.has(req.id));

  // Calculate stats
  const getTotalPendingDocuments = () => {
    return selectedRequestsData.reduce((total, req) => 
      total + req.items.filter(item => item.status === 'pending' || item.status === 'rejected').length,
      0
    );
  };

  const getTotalUploadedFiles = () => {
    let count = 0;
    selectedRequestsData.forEach(req => {
      const requestUploads = uploads[req.id];
      if (requestUploads) {
        Object.values(requestUploads).forEach(itemUploads => {
          count += itemUploads.filter(u => !u.error).length;
        });
      }
    });
    return count;
  };

  const getTotalUploadSize = () => {
    let size = 0;
    selectedRequestsData.forEach(req => {
      const requestUploads = uploads[req.id];
      if (requestUploads) {
        Object.values(requestUploads).forEach(itemUploads => {
          itemUploads.forEach(upload => {
            if (!upload.error) {
              size += upload.file.size;
            }
          });
        });
      }
    });
    return size;
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const failureCount = results.filter(r => r.status === 'failed').length;
  const partialCount = results.filter(r => r.status === 'partial').length;

  // Selection Step
  if (step === 'selection') {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-hidden z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full h-[90vh] max-w-7xl flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <CloudArrowUpIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Bulk Manual Document Upload</h2>
                  <p className="text-sm text-gray-600 mt-1">Upload documents for multiple leads at once</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
              >
                <XMarkIcon className="w-7 h-7" />
              </button>
            </div>
          </div>

          {error && (
            <div className="flex-shrink-0 mx-8 mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col px-8 py-6">
            {/* Search and Filters */}
            <div className="flex-shrink-0 grid grid-cols-3 gap-4 mb-6">
              <div className="col-span-2 relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by lead name, email, or UID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div className="relative">
                <FunnelIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none"
                >
                  <option value="requested">Requested</option>
                  <option value="submitted">Submitted</option>
                  <option value="rejected">Rejected</option>
                  <option value="">All Statuses</option>
                </select>
              </div>
            </div>

            {/* Info Banner about Limit */}
            <div className="flex-shrink-0 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                ℹ️ <strong>Selection Limit:</strong> You can select up to 20 document requests at a time for optimal upload performance.
              </p>
            </div>

            {/* Selection Header */}
            <div className="flex-shrink-0 flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <h3 className="text-lg font-semibold text-gray-900">Select Document Requests</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedRequests.size >= 20 
                    ? 'bg-red-100 text-red-700 border-2 border-red-300' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {selectedRequests.size} / 20 selected
                </span>
                {selectedRequests.size >= 20 && (
                  <span className="text-xs text-red-600 font-medium">
                    ⚠️ Maximum limit reached
                  </span>
                )}
              </div>
              <button
                onClick={handleSelectAll}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                {selectedRequests.size === filteredRequests.length && filteredRequests.length > 0
                  ? 'Clear All'
                  : filteredRequests.length > 20
                  ? `Select First 20 (of ${filteredRequests.length})`
                  : `Select All (${filteredRequests.length})`}
              </button>
            </div>

            {/* Requests List */}
            <div className="flex-1 overflow-y-auto space-y-3 mb-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredRequests.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Requests Found</h3>
                  <p className="text-gray-600">
                    {searchTerm 
                      ? 'No requests match your search criteria' 
                      : 'No document requests available for manual upload'}
                  </p>
                </div>
              ) : (
                filteredRequests.map((req) => {
                  const isSelected = selectedRequests.has(req.id);
                  const pendingItems = req.items.filter(item => 
                    item.status === 'pending' || item.status === 'rejected'
                  );

                  return (
                    <div
                      key={req.id}
                      className={`border-2 rounded-xl transition-all ${
                        isSelected 
                          ? 'border-blue-400 bg-blue-50 shadow-md' 
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="p-5">
                        <div className="flex items-start space-x-4">
                          <input
                            type="checkbox"
                            id={`request-${req.id}`}
                            checked={isSelected}
                            onChange={() => handleRequestToggle(req.id)}
                            disabled={!isSelected && selectedRequests.size >= 20}
                            className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <div className="flex-1">
                            <label 
                              htmlFor={`request-${req.id}`} 
                              className={`${!isSelected && selectedRequests.size >= 20 ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <UserIcon className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <div className="flex items-center space-x-2">
                                      <h4 className="text-base font-semibold text-gray-900">
                                        {req.lead.firstName} {req.lead.lastName}
                                      </h4>
                                      {req.lead.leadUid && (
                                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                          {req.lead.leadUid}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-600">{req.lead.email}</p>
                                    <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                                      <span>Request #{req.id}</span>
                                      <span>•</span>
                                      <span className="flex items-center">
                                        <ClockIcon className="w-3 h-3 mr-1" />
                                        {formatDate(req.requestedAt)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  req.status === 'requested' ? 'bg-yellow-100 text-yellow-800' :
                                  req.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                                  req.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {req.status}
                                </span>
                              </div>
                            </label>

                            {/* Pending Documents */}
                            <div className="mt-4 flex flex-wrap gap-2">
                              {pendingItems.map(item => (
                                <div
                                  key={item.id}
                                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium ${
                                    item.status === 'rejected'
                                      ? 'bg-red-100 text-red-700 border border-red-200'
                                      : 'bg-gray-100 text-gray-700 border border-gray-200'
                                  }`}
                                >
                                  <DocumentTextIcon className="w-3.5 h-3.5 mr-1.5" />
                                  {item.documentType.name}
                                  {item.documentType.isRequired && (
                                    <span className="ml-1.5 text-red-600">*</span>
                                  )}
                                  {item.status === 'rejected' && (
                                    <XCircleIcon className="w-3.5 h-3.5 ml-1.5" />
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Show rejection reasons if any */}
                            {isSelected && req.items.some(item => item.status === 'rejected' && item.notes && item.notes.length > 0) && (
                              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-xs font-semibold text-yellow-900 mb-2">⚠️ Rejection Reasons:</p>
                                {req.items
                                  .filter(item => item.status === 'rejected' && item.notes && item.notes.length > 0)
                                  .map(item => (
                                    <div key={item.id} className="text-xs text-yellow-800 mb-1">
                                      <strong>{item.documentType.name}:</strong> {item.notes![0].note}
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Summary Footer */}
            {selectedRequests.size > 0 && (
              <div className="flex-shrink-0 p-5 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <div className="text-3xl font-bold text-blue-600">{selectedRequests.size}</div>
                    <div className="text-sm text-gray-700">Requests Selected</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-purple-600">{getTotalPendingDocuments()}</div>
                    <div className="text-sm text-gray-700">Documents Pending</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-green-600">{selectedRequestsData.reduce((sum, req) => sum + (req.items.filter(i => i.status === 'rejected').length), 0)}</div>
                    <div className="text-sm text-gray-700">Rejected (Need Re-upload)</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex-shrink-0 px-8 py-5 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleNext}
              disabled={selectedRequests.size === 0}
              className="px-8 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              Next: Upload Documents
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Upload Step
  if (step === 'upload') {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-hidden z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full h-[90vh] max-w-7xl flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center shadow-lg">
                  <DocumentTextIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Upload Documents</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Step 2 of 3 - Upload files for {selectedRequests.size} selected request(s)
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
              >
                <XMarkIcon className="w-7 h-7" />
              </button>
            </div>
          </div>

          {error && (
            <div className="flex-shrink-0 mx-8 mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Content - Scrollable Upload Area */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <div className="space-y-6">
              {selectedRequestsData.map((req) => {
                const isExpanded = expandedRequests.has(req.id);
                const pendingItems = req.items.filter(item => 
                  item.status === 'pending' || item.status === 'rejected'
                );
                const requestUploads = uploads[req.id] || {};
                const hasUploads = Object.values(requestUploads).some(items => items.length > 0);

                return (
                  <div key={req.id} className="border-2 border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                    {/* Request Header */}
                    <div
                      onClick={() => toggleRequestExpanded(req.id)}
                      className="p-5 bg-gradient-to-r from-gray-50 to-slate-50 cursor-pointer hover:from-gray-100 hover:to-slate-100 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <UserIcon className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <h4 className="text-lg font-semibold text-gray-900">
                                {req.lead.firstName} {req.lead.lastName}
                              </h4>
                              {req.lead.leadUid && (
                                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                                  {req.lead.leadUid}
                                </span>
                              )}
                              {hasUploads && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex items-center">
                                  <CheckCircleIcon className="w-3 h-3 mr-1" />
                                  Files Ready
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{req.lead.email}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {pendingItems.length} document type(s) pending
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-600">
                            Click to {isExpanded ? 'collapse' : 'expand'}
                          </span>
                          <svg
                            className={`w-6 h-6 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Document Upload Areas */}
                    {isExpanded && (
                      <div className="p-6 space-y-5 bg-white">
                        {pendingItems.map((item) => {
                          const itemUploads = requestUploads[item.id] || [];
                          const validUploads = itemUploads.filter(u => !u.error);

                          return (
                            <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <DocumentTextIcon className="w-5 h-5 text-gray-600" />
                                    <h5 className="text-sm font-semibold text-gray-900">
                                      {item.documentType.name}
                                    </h5>
                                    {item.documentType.isRequired && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Required
                                      </span>
                                    )}
                                    {item.status === 'rejected' && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                        <XCircleIcon className="w-3 h-3 mr-1" />
                                        Rejected
                                      </span>
                                    )}
                                  </div>
                                  {item.documentType.description && (
                                    <p className="text-xs text-gray-600">{item.documentType.description}</p>
                                  )}
                                  <p className="text-xs text-gray-500 mt-1">
                                    Max: {item.documentType.maxFileSizeMb}MB | 
                                    Allowed: {item.documentType.allowedFileTypes.join(', ')}
                                  </p>
                                  
                                  {/* Show rejection reason */}
                                  {item.status === 'rejected' && item.notes && item.notes.length > 0 && (
                                    <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                                      <strong>Rejection Reason:</strong> {item.notes[0].note}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* File Upload Zone */}
                              <div className="mb-3">
                                <input
                                  type="file"
                                  id={`file-${req.id}-${item.id}`}
                                  multiple
                                  accept={item.documentType.allowedFileTypes.map(t => `.${t}`).join(',')}
                                  onChange={(e) => handleFileSelect(req.id, item.id, e.target.files)}
                                  className="hidden"
                                />
                                <label
                                  htmlFor={`file-${req.id}-${item.id}`}
                                  className="block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-all"
                                >
                                  <CloudArrowUpIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                                  <p className="text-sm font-medium text-gray-700 mb-1">
                                    Click to upload or drag and drop
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Multiple files supported
                                  </p>
                                </label>
                              </div>

                              {/* Uploaded Files List */}
                              {itemUploads.length > 0 && (
                                <div className="space-y-2">
                                  <p className="text-xs font-semibold text-gray-700">Selected Files:</p>
                                  {itemUploads.map((upload, index) => (
                                    <div
                                      key={index}
                                      className={`flex items-center justify-between p-3 rounded-lg ${
                                        upload.error ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'
                                      }`}
                                    >
                                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                                        {upload.error ? (
                                          <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
                                        ) : (
                                          <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate">
                                            {upload.file.name}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            {formatFileSize(upload.file.size)}
                                          </p>
                                          {upload.error && (
                                            <p className="text-xs text-red-600 mt-1">{upload.error}</p>
                                          )}
                                        </div>
                                      </div>
                                      <button
                                        onClick={() => removeFile(req.id, item.id, index)}
                                        className="ml-3 text-red-600 hover:text-red-800 flex-shrink-0"
                                      >
                                        <TrashIcon className="w-5 h-5" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Note Field */}
                              <div className="mt-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Note for this document (Optional)
                                </label>
                                <textarea
                                  value={notes[req.id]?.[item.id] || ''}
                                  onChange={(e) => setNotes(prev => ({
                                    ...prev,
                                    [req.id]: {
                                      ...(prev[req.id] || {}),
                                      [item.id]: e.target.value
                                    }
                                  }))}
                                  rows={2}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Add any notes about this document..."
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upload Summary Footer */}
          {selectedRequests.size > 0 && (
            <div className="flex-shrink-0 px-8 py-5 bg-gradient-to-br from-purple-50 to-pink-50 border-t border-purple-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-8">
                  <div>
                    <div className="text-sm text-gray-600">Total Files Ready</div>
                    <div className="text-2xl font-bold text-purple-600">{getTotalUploadedFiles()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Size</div>
                    <div className="text-2xl font-bold text-pink-600">{formatFileSize(getTotalUploadSize())}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Requests</div>
                    <div className="text-2xl font-bold text-indigo-600">{selectedRequests.size}</div>
                  </div>
                </div>
                {getTotalUploadedFiles() === 0 && (
                  <div className="flex items-center space-x-2 text-orange-600 bg-orange-50 px-4 py-2 rounded-lg border border-orange-200">
                    <ExclamationTriangleIcon className="w-5 h-5" />
                    <span className="text-sm font-medium">No files uploaded yet</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex-shrink-0 px-8 py-5 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <button
              onClick={handleBack}
              className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Selection
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || getTotalUploadedFiles() === 0}
              className="px-8 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                  Upload {getTotalUploadedFiles()} File(s)
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Progress Step
  if (step === 'progress') {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-hidden z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full h-[70vh] max-w-4xl flex flex-col">
          <div className="flex-1 flex flex-col items-center justify-center p-12">
            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Uploading Documents</h3>
            <p className="text-lg text-gray-600 mb-2">{progress.currentRequest}</p>
            <p className="text-sm text-gray-500 mb-8">
              Please wait while we upload your documents...
            </p>

            {/* Progress Bar */}
            <div className="w-full max-w-2xl">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Request {progress.current} of {progress.total}
                </span>
                <span className="text-sm font-medium text-blue-600">
                  {progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 shadow-inner">
                <div
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 h-4 rounded-full transition-all duration-500 shadow-lg"
                  style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                ></div>
              </div>
              <div className="mt-4 flex items-center justify-between text-xs text-gray-600">
                <span>✅ Completed: {progress.current}</span>
                <span>⏳ Remaining: {progress.total - progress.current}</span>
              </div>
            </div>

            <div className="mt-8 text-center text-sm text-gray-500">
              <p>⚠️ Please don't close this window while uploading</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results Step
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-hidden z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full h-[90vh] max-w-7xl flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center shadow-lg">
                <CheckCircleIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Upload Complete</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {successCount} of {results.length} request(s) uploaded successfully
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                onUploadSuccess();
                onClose();
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
            >
              <XMarkIcon className="w-7 h-7" />
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="flex-shrink-0 px-8 py-6">
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl p-5">
              <div className="text-4xl font-bold text-blue-600">{results.length}</div>
              <div className="text-sm font-medium text-blue-900 mt-1">Total Processed</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 rounded-xl p-5">
              <div className="text-4xl font-bold text-green-600">{successCount}</div>
              <div className="text-sm font-medium text-green-900 mt-1">Successful</div>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200 rounded-xl p-5">
              <div className="text-4xl font-bold text-red-600">{failureCount}</div>
              <div className="text-sm font-medium text-red-900 mt-1">Failed</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-xl p-5">
              <div className="text-4xl font-bold text-purple-600">
                {formatFileSize(results.reduce((sum, r) => sum + r.totalSize, 0))}
              </div>
              <div className="text-sm font-medium text-purple-900 mt-1">Total Uploaded</div>
            </div>
          </div>
        </div>

        {/* Results List */}
        <div className="flex-1 overflow-y-auto px-8 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Details</h3>
          <div className="space-y-3">
            {results.map((result) => (
              <div
                key={result.requestId}
                className={`border-2 rounded-xl p-5 ${
                  result.status === 'success' 
                    ? 'border-green-200 bg-green-50' 
                    : result.status === 'partial'
                    ? 'border-orange-200 bg-orange-50'
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {result.status === 'success' ? (
                      <CheckCircleIcon className="w-8 h-8 text-green-600" />
                    ) : result.status === 'partial' ? (
                      <ExclamationTriangleIcon className="w-8 h-8 text-orange-600" />
                    ) : (
                      <XCircleIcon className="w-8 h-8 text-red-600" />
                    )}
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">{result.leadName}</h4>
                      <p className="text-sm text-gray-600">Request #{result.requestId}</p>
                      {result.status === 'success' && (
                        <p className="text-sm text-green-700 mt-1">
                          ✅ Uploaded {result.uploadedFiles} file(s) ({formatFileSize(result.totalSize)})
                        </p>
                      )}
                      {result.status === 'partial' && (
                        <p className="text-sm text-orange-700 mt-1">
                          ⚠️ {result.uploadedFiles} succeeded, {result.failedFiles} failed
                        </p>
                      )}
                      {result.status === 'failed' && (
                        <p className="text-sm text-red-700 mt-1">
                          ❌ {result.error || 'Upload failed'}
                        </p>
                      )}
                    </div>
                  </div>
                  {result.status !== 'success' && (
                    <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-red-100 text-red-700">
                      Failed
                    </span>
                  )}
                </div>

                {/* Show detailed item errors */}
                {result.itemErrors && result.itemErrors.length > 0 && (
                  <div className="mt-3 ml-12 space-y-1">
                    {result.itemErrors.map((itemError, idx) => (
                      <div key={idx} className="text-xs text-red-700">
                        • <strong>{itemError.documentType}:</strong> {itemError.fileName} - {itemError.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 px-8 py-5 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          {failureCount > 0 && (
            <button
              onClick={handleRetryFailed}
              className="px-6 py-3 border border-orange-600 rounded-lg text-sm font-medium text-orange-600 bg-white hover:bg-orange-50 transition-colors"
            >
              Retry Failed Uploads ({failureCount})
            </button>
          )}
          <div className="flex-1"></div>
          <button
            onClick={() => {
              onUploadSuccess();
              onClose();
            }}
            className="px-8 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkManualDocumentUploadModal;
